import styled from '@emotion/styled';
import { Button, Grid } from '@mui/material';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  setActiveTitleId as setActiveTitleIdV2,
  setActiveTitleLevel as setActiveTitleLevelV2,
  setSelectedTitleId as setSelectedTitleIdV2,
  updateActiveTitleHierarchy as updateActiveTitleHierarchyV2
} from '@/redux/slices/titleV2Slice';
import {
  selectActiveTitleHierarchy,
  selectActiveTitleId,
  selectActiveTitleLevel,
  selectTitle,
  selectTitleDataById
} from '@/redux/selectors/titleSelectors';
import {
  selectActiveTitleHierarchy as selectActiveTitleHierarchyV2,
  selectActiveTitleId as selectActiveTitleIdV2,
  selectActiveTitleLevel as selectActiveTitleLevelV2,
  selectRootTitleId,
  selectTitlesById as selectTitlesByIdV2
} from '@/redux/selectors/titleV2Selectors';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import { PolarisIcon } from '@/exports/columnCommonComponents';
import { setActiveTitle, setActiveTitleLevel, setSelectedTitleId, updateActiveTitleHierarchy } from '@/redux/slices/titleSlice';

const OPStep2Breadcrumbs = () => {
  const dispatch = useAppDispatch();
  const isTitleV2 = useConfigValue(['titleConfig', 'v2']);
  const rootTitle = useAppSelector(selectTitle);
  const rootTitleId = isTitleV2 ? useAppSelector(selectRootTitleId) : rootTitle?.id || 0;
  const activeTitleId = isTitleV2 ? useAppSelector(selectActiveTitleIdV2) : useAppSelector(selectActiveTitleId);
  const activeTitleLevel = isTitleV2 ? useAppSelector(selectActiveTitleLevelV2) : useAppSelector(selectActiveTitleLevel);
  const activeTitleHierarchy = isTitleV2 ? useAppSelector(selectActiveTitleHierarchyV2) : useAppSelector(selectActiveTitleHierarchy);
  const titlesById = isTitleV2 ? useAppSelector(selectTitlesByIdV2) : useAppSelector(selectTitleDataById);
  if (!activeTitleLevel || activeTitleLevel < 3) return null;

  const updateActiveTitle = (newTitleId: number, newLevel: number) => {
    if (activeTitleId === newTitleId) return;

    dispatch(isTitleV2 ? setSelectedTitleIdV2(newTitleId) : setSelectedTitleId(newTitleId));
    if (newLevel < 3) {
      dispatch(isTitleV2 ? setActiveTitleIdV2(rootTitleId) : setActiveTitle(rootTitleId));
      dispatch(isTitleV2 ? setActiveTitleLevelV2(newLevel) : setActiveTitleLevel(newLevel));
      dispatch(
        isTitleV2
          ? updateActiveTitleHierarchyV2({ id: rootTitleId as number, level: newLevel })
          : updateActiveTitleHierarchy({ id: rootTitleId as number, level: newLevel })
      );
      return;
    }

    dispatch(isTitleV2 ? setActiveTitleIdV2(newTitleId) : setActiveTitle(newTitleId));
    dispatch(isTitleV2 ? setActiveTitleLevelV2(newLevel) : setActiveTitleLevel(newLevel));
    dispatch(
      isTitleV2 ? updateActiveTitleHierarchyV2({ id: newTitleId, level: newLevel }) : updateActiveTitleHierarchy({ id: newTitleId, level: newLevel })
    );
  };

  return (
    <BreadcrumbsContainer container>
      {activeTitleHierarchy.map((titleId, index) => (
        <BreadcrumbsMenuContainer item key={titleId}>
          <BreadcrumbsMenu variant="text" disabled={activeTitleLevel === index + 1} onClick={() => updateActiveTitle(titleId, index + 1)}>
            {titlesById[titleId]?.name || 'Untitled'}
          </BreadcrumbsMenu>
          {index < activeTitleHierarchy.length - 1 && <PolarisIcon icon="right" height={16} />}
        </BreadcrumbsMenuContainer>
      ))}
    </BreadcrumbsContainer>
  );
};

export default OPStep2Breadcrumbs;

const BreadcrumbsContainer = styled(Grid)(({ theme }) => ({
  alignItems: 'center',
  padding: theme.spacing(1),
  gap: theme.spacing(0.5),
  marginBottom: theme.spacing(2)
}));

const BreadcrumbsMenuContainer = styled(Grid)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5)
}));

const BreadcrumbsMenu = styled(Button)<{ disabled: boolean }>(({ theme, disabled }) => ({
  padding: 0,
  cursor: disabled ? 'default' : 'pointer',
  color: disabled ? theme.palette.text.disabled : theme.palette.primary.main,
  '&:hover': {
    textDecoration: disabled ? 'none' : 'underline'
  }
}));
